import { createContext, useContext, useRef, useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaEdit, FaPlus, FaInfoCircle } from 'react-icons/fa';
import { useMembers } from '../../hooks/useMembers';
import { useMessages } from '../../contexts/messages';
import { filterInboxesByPermissions } from '../../components/Inbox/utils';
import { useModal } from '../../contexts/modal';
import { useFish } from '../../contexts/fish';
import { DateTime } from 'luxon';
import Decimal from 'decimal.js';
import { formatPhone, getDisplayMemberSubscriptionStatus } from '../../utils';
import Modal from '@Components/Modal';
import SMSThread from '@Components/SMSThread';
import SMSThreadSendBox from '@Components/SMSThreadSendBox';
import TagMultiSelectDropdown from '@Components/TagMultiSelectDropdown';
import Select from 'react-select';
import ActivityList from '@Components/ActivityList';
import { Card } from '@Components/Card/index';
import FishCard from '@Components/FishCard/index';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@Components/Tabs/index';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@Components/Breadcrumb/index';
import service from '../../service';
import Receipt from '@Components/Receipt/index';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogClose,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import LoadingSpinner from '@Components/LoadingSpinner';
import InteractionList from '@Components/InteractionList';
import ManualCharge from '@Components/ManualCharge';
import { useAuth } from '../../contexts/auth';
import { Switch } from '@Components/Switch/index';
import {
  Toaster,
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useActivity } from '../../hooks/useActivity';
import ShowFor from '@Components/ShowFor';
import ReferralList from '@Components/ReferralList';
import ProfileInsights from '@Components/ProfileInsights';
import { usePaymentSources } from '../../hooks/usePaymentSources';
import { useSecret } from '../../stores/secretsStore';

const FishDetailsContext = createContext(null);

/** Shared state for the fish details shell, banner, panels and action dialogs. */
export const useFishDetails = () => useContext(FishDetailsContext);

export function FishDetailsProvider({ children, fishId, variant }) {
  const { id: fishRouteId, teamMemberId } = useParams();
  const id = fishId ?? fishRouteId ?? teamMemberId;
  const { pathname } = useLocation();
  const isTeamMemberRoute = pathname.toLowerCase().includes('team-members');
  const effectiveVariant =
    variant ??
    (isTeamMemberRoute
      ? 'team-member'
      : pathname.includes('members')
        ? 'member'
        : 'guest');
  const navigate = useNavigate();
  const {
    readFishTags,
    readSegmentsForFish,
    segmentsForFish,
    getMemberById,
    fishTags,
    getMemberSpendAmounts,
  } = useFish();
  const {
    inboxes,
    fetchAllInboxes,
    fetchInboxSmsThreads,
    fetchSmsThread,
    userPermissions,
    fetchUserPermissions,
  } = useMessages();
  const { openModal, closeModal } = useModal();
  const { user } = useAuth();
  const [detailsType, setDetailsType] = useState(null);
  const [actor, setActor] = useState(null);
  const [modalContent, setModalContent] = useState(null);
  const [selectedInbox, setSelectedInbox] = useState(null);
  const [smsThread, setSmsThread] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [receiptData, setReceiptData] = useState({});
  const [adjustAmount, setAdjustAmount] = useState(null);

  const [adjustDisabled, setAdjustDisabled] = useState(false);
  const [finalizeAdjustDisabled, setFinalizeAdjustDisabled] = useState(false);
  const [adjustError, setAdjustError] = useState(true);
  const [dryError, setDryError] = useState('');
  const [adjustSuccess, setAdjustSuccess] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [adjustData, setAdjustData] = useState({});

  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const [interactions, setInteractions] = useState();

  const [hasFetchedActor, setHasFetchedActor] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [homeBaseCopy, setHomeBaseCopy] = useState(null);
  const [homeBaseMessage, setHomeBaseMessage] = useState('');

  //Shared Subscription Tab state vars
  const [selectedFishSubToAdd, setSelectedFishSubToAdd] = useState(null);
  const [otherFishOnSubscription, setOtherFishOnSubscription] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteNameForSharedSub, setDeleteNameForSharedSub] = useState('');
  const [sharedTabMessage, setSharedTabMessage] = useState(null);
  const [fishCardIndex, setFishCardIndex] = useState(null);
  const [sharedSubSearchTerm, setSharedSubSearchTerm] = useState('');
  const [sharedSubInputValue, setSharedSubInputValue] = useState('');

  //vars for cancelling subscriptions
  const [isCancelSubOpen, setIsCancelSubOpen] = useState(false);
  const [confirmCancelText, setConfirmCancelText] = useState(null);

  //badge status and subscription details status vars
  const [displaySubscriptionStatus, setDisplaySubscriptionStatus] = useState({
    status: '',
    color: '',
  });

  const sharedSubTimeoutRef = useRef(null);

  // Activity hook with showCancelled toggle
  const {
    activity,
    upcomingCount: upcomingActivityCount,
    showCancelled,
    setShowCancelled,
    isLoading: isActivityLoading,
  } = useActivity({ fishId: actor?.id, enabled: !!actor?.id });

  const restaurantGuid = useSecret('TOAST_MANUAL_CHARGE_RESTAURANT_GUID');
  const { paymentSources, refetch: fetchPaymentSources } = usePaymentSources({
    fishId: id,
    restaurantGuid,
    enabled: !isTeamMemberRoute && pathname.includes('members'),
  });
  const walletBalance =
    paymentSources?.wallets.find((wallet) => wallet.owner === 'OWN')
      ?.balance ?? null;

  const homeBaseOptions = [
    {
      label: 'Hudson House',
      value: 'HUDSON_HOUSE',
      image: '/felina_hudson_house.jpg',
    },
    { label: 'Logan Inn', value: 'LOGAN_INN', image: '/logan_inn.jpg' },
    { label: 'Ryland Inn', value: 'RYLAND_INN', image: '/ryland_inn.jpg' },
    {
      label: 'The Albion Summit',
      value: 'ALBION_SUMMIT',
      image: '/albion_summit_room.jpeg',
    },
  ];
  const selectedHomeBase = homeBaseOptions.find(
    (venue) => venue.value === actor?.memberProfile?.homeBase,
  );

  const hasSubs =
    Array.isArray(actor?.subscriptions) && actor?.subscriptions?.length;
  const activeSubscription = hasSubs ? actor.subscriptions[0] : {};

  useEffect(() => {
    fetchActor();
  }, [id]);

  useEffect(() => {
    const fetchDiscountInfo = async () => {
      if (activeSubscription?.registrationCodeId) {
        const registrationCodeData = await service.getRegistrationCodeById(
          activeSubscription?.registrationCodeId,
        );
        if (registrationCodeData) {
          const hasCouplesDiscount =
            registrationCodeData?.allowedSubTierIds?.includes(
              activeSubscription?.subscriptionTier?.id,
            ) && registrationCodeData.code === 'STORKCOUPLE';

          const updatedDiscountInfo = {
            ...registrationCodeData,
            hasRenewed: !!activeSubscription?.renewedAt,
            hasCouplesDiscount,
          };

          setActor((prev) => ({
            ...prev,
            discountInfo: updatedDiscountInfo,
          }));

          getPriceAfterDiscount(updatedDiscountInfo);
        }
      }
      if (isLoading) {
        setIsLoading(false);
      }
    };
    if (hasFetchedActor) {
      fetchDiscountInfo();
      getFishInteractions();
    }
  }, [hasFetchedActor]);

  // Restrict SMS senders to the inboxes this user may send through — the same
  // permission model the /inbox view uses.
  const permittedInboxes = useMemo(
    () =>
      userPermissions
        ? filterInboxesByPermissions(inboxes, userPermissions, user?.id)
        : [],
    [inboxes, userPermissions, user?.id],
  );

  // The SMS picker defaults to the current user's own SMS inbox, like /inbox.
  useEffect(() => {
    const smsInboxes = permittedInboxes.filter((inbox) => inbox.inboxType === 'SMS');
    if (smsInboxes.some((inbox) => inbox.id === selectedInbox?.id)) return;
    setSelectedInbox(
      smsInboxes.find((inbox) => inbox.userId === user?.id) ?? smsInboxes[0] ?? null,
    );
  }, [permittedInboxes, selectedInbox, user?.id]);

  useEffect(() => {
    const fetchAllMessages = async () => {
      const messagesData = await fetchSmsThread(actor?.phoneNumber);
      if (messagesData) {
        setAllMessages(messagesData);
      }
    };

    const fetchThread = async () => {
      if (selectedInbox) {
        try {
          const threads = await fetchInboxSmsThreads(selectedInbox.id);

          //Using .includes() to also identify matches that have a +1 prefix
          const actorThread = threads.find((thread) =>
            thread.phoneNumber.includes(actor.phoneNumber),
          );

          if (actorThread && actorThread.SMSThread) {
            setSmsThread(actorThread.SMSThread);
          } else {
            setSmsThread([]);
          }
        } catch (error) {
          console.error('Error fetching SMS thread: ', error);
          setSmsThread([]);
        }
      }
    };

    if (actor?.phoneNumber) {
      fetchAllMessages();
    }

    if (modalContent === 'sms') {
      fetchThread();
    }
  }, [actor?.phoneNumber, selectedInbox, modalContent, fetchInboxSmsThreads]);

  useEffect(() => {
    if (isTeamMemberRoute) {
      setDetailsType('TeamMembers');
    } else if (pathname.toLowerCase().includes('members')) {
      setDetailsType('Members');
    } else if (pathname.toLowerCase().includes('guests')) {
      setDetailsType('Fish');
    }
  }, [pathname, isTeamMemberRoute]);

  useEffect(() => {
    if (actor?.subscriptions) {
      const statusResult = getDisplayMemberSubscriptionStatus(
        actor.subscriptions,
      );
      setDisplaySubscriptionStatus(statusResult);
    }
  }, [actor?.subscriptions]);

  const formatDisplayNameForFishCard = (subscriptionOrFish) => {
    const memberIndexRaw = subscriptionOrFish?.fishProfiles?.findIndex(
      (profile) => profile.profileType === 'MEMBER',
    );
    const memberIndex = memberIndexRaw >= 0 ? memberIndexRaw : 0;

    const displayResult = subscriptionOrFish?.memberProfile
      ? subscriptionOrFish?.memberProfile?.fName +
        ' ' +
        subscriptionOrFish?.memberProfile?.lName
      : subscriptionOrFish?.fishProfiles[memberIndex]?.fName +
        ' ' +
        subscriptionOrFish?.fishProfiles[memberIndex]?.lName;

    if (!displayResult) {
      return 'N/A';
    } else {
      return displayResult
        ?.split(' ')
        .map((word) => {
          if (word?.length === 0) return '';
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
    }
  };

  const getPreferredProfile = (fish) => {
    if (!fish || !fish.fishProfiles || fish.fishProfiles.length === 0) {
      return null;
    }
    const memberProfile = fish.fishProfiles.find(
      (profile) => profile.profileType === 'MEMBER',
    );
    if (memberProfile) {
      return memberProfile;
    }
    const sortedProfiles = [...fish.fishProfiles].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    return sortedProfiles[0] || fish.fishProfiles[0];
  };

  const preferredProfile = getPreferredProfile(actor);
  const employeeProfile =
    actor?.fishProfiles?.find((profile) => profile?.profileType === 'EMPLOYEE') ||
    actor?.fishProfiles?.[0];
  const aiSummary = preferredProfile?.additionalMeta?.fishSummary?.summary;
  const fullContact = preferredProfile?.additionalMeta?.fullContact;
  const capturedForms = preferredProfile?.additionalMeta?.forms;

  const isSearching = Boolean(sharedSubSearchTerm?.trim());
  const { data: cachedFish, status } = useMembers({
    searchParams: {
      'filter[phoneNumber]': `*${sharedSubSearchTerm}*`,
      'include': ['subscriptions', 'sharedSubscription'],
    },
    enabled: sharedSubSearchTerm?.length > 2,
  });

  const fishOptions = cachedFish?.data
    ?.filter(
      (fish) =>
        fish.id !== actor.id &&
        !otherFishOnSubscription?.some((sub) => sub?.fishId === fish?.id) &&
        (!fish?.sharedSubscription?.length || !fish?.sharedSubscription) &&
        !fish?.subscriptions?.some((sub) => sub?.fishId !== fish?.id), //remove me when backend works with sharedSubscription filtering from useQuery
    )
    ?.map((fish) => ({
      ...fish,
      value: fish.id,
      label:
        formatDisplayNameForFishCard(fish) +
        ' - ' +
        formatPhone(fish.phoneNumber),
    }));

  const dryRunAdjustWallet = async () => {
    setAdjustDisabled(true);
    setDryError('');

    if (!adjustAmount) {
      setAdjustDisabled(false);
      setDryError('Amount is incorrect.');
      return;
    }

    const res = await service.adjustWalletBalance(id, adjustAmount, true);
    const amount = await res.json();

    const fieldOptions = await service.getFieldOptions();
    const cleanedOptions = {};
    fieldOptions.map((option) => {
      const category = option.category.replace('_', ' ');
      if (cleanedOptions[category] === undefined) {
        cleanedOptions[category] = {
          label: category,
          options: [],
        };
      }
      cleanedOptions[category].options.push({
        value: option.value,
        label: option.label,
      });
    });

    amount.options = Object.values(cleanedOptions);

    setAdjustData(amount);
    setIsConfirmOpen(true);
    setAdjustError('');
  };

  const adjustWallet = async (e) => {
    setFinalizeAdjustDisabled(true);
    const res = await service.adjustWalletBalance(
      id,
      adjustAmount,
      false,
      reason.value,
      notes,
    );
    const amount = await res.json();

    if (!res.ok) {
      console.error(amount);
      setFinalizeAdjustDisabled(false);
      setAdjustError(amount.message);
      return;
    }

    setAdjustSuccess(
      `Successfully Adjusted Wallet by ${parseFloat(
        adjustAmount,
      ).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })} to ${parseFloat(amount.newBalance).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })}`,
    );
    setAdjustDisabled(false);
    setIsConfirmOpen(false);
    setAdjustAmount('');
    setFinalizeAdjustDisabled(false);
    fetchPaymentSources();
    fetchActor();
  };

  const handleOpenChange = (open) => {
    if (!open) {
      setFinalizeAdjustDisabled(false);
      setAdjustDisabled(false);
      setIsConfirmOpen(false);
      setAdjustAmount('');
    }
  };

  const fetchActor = async () => {
    try {
      if (!isLoading) {
        setIsLoading(true);
      }
      let actor;

      if (effectiveVariant === 'member') {
        actor = await getMemberById(id);
      } else {
        actor = await service.readFishById(id, true);
      }

      if (actor) {
        if (actor?.subscriptions?.length) {
          const actorWithSpendsArr = await getMemberSpendAmounts(actor);
          const actorWithSpends = Array.isArray(actorWithSpendsArr)
            ? actorWithSpendsArr[0]
            : actorWithSpendsArr;

          if (actorWithSpends) {
            setActor((prev) => ({
              ...prev,
              ...actorWithSpends,
            }));
          }
        } else {
          setActor((prev) => ({
            ...prev,
            ...actor,
          }));
        }
      }
      await readFishTags(id);
      await readSegmentsForFish(id);
      await fetchAllInboxes();
      if (user?.id) await fetchUserPermissions(user.id);

      setHasFetchedActor(true);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.error('Error while fetching member', err);
    }
  };

  const getFishInteractions = async () => {
    const results = await service.getFlowStageResultsByFishId(id);
    if (!results.ok) return;

    const flowStageResults = await results.json();
    setInteractions(flowStageResults);
  };

  const handleInboxChange = (selectedOption) => {
    const inbox = permittedInboxes.find((inbox) => inbox.id === selectedOption.value);
    setSelectedInbox(inbox);
  };

  const getPurchaseTypeDisplay = (purchase) => {
    if (!purchase.productDescription) {
      return 'Purchase';
    }

    switch (purchase.productDescription) {
      case 'DINE_IN':
        return 'Dine In';
      default:
        return purchase.productDescription;
    }
  };

  const getPriceAfterDiscount = (discountInfo) => {
    const currentDate = DateTime.now().setZone('utc');
    const startingTime = DateTime.fromISO(
      activeSubscription?.renewedAt === null
        ? activeSubscription?.beginsAt
        : activeSubscription?.renewedAt,
      { zone: 'utc' },
    );

    let subscriptionDiscountEndsAt;
    let subscriptionDiscountHasEnded;
    let subscriptionPriceAfterDiscount;

    let walletDiscountEndsAt;
    let walletDiscountHasEnded;
    let walletPriceAfterDiscount;

    if (discountInfo?.hasRenewed) {
      if (Number(discountInfo.subsequentSubscriptionDiscountAmount === 0)) {
        subscriptionDiscountEndsAt = 'No subsequent discount applicable';
        subscriptionDiscountHasEnded = true;
      } else if (
        discountInfo?.subsequentSubscriptionDiscountDurationUnit === 'DAY'
      ) {
        subscriptionDiscountEndsAt = startingTime.plus({
          days: discountInfo.subsequentSubscriptionDiscountDurationAmount,
        });
      } else if (
        discountInfo?.subsequentSubscriptionDiscountDurationUnit === 'MONTH'
      ) {
        subscriptionDiscountEndsAt = startingTime.plus({
          months: discountInfo.subsequentSubscriptionDiscountDurationAmount,
        });
      } else if (
        discountInfo?.subsequentSubscriptionDiscountDurationUnit === 'YEAR'
      ) {
        subscriptionDiscountEndsAt = startingTime.plus({
          years: discountInfo.subsequentSubscriptionDiscountDurationAmount,
        });
      } else {
        console.error('No subsequent subscription info found');
      }
    } else {
      if (activeSubscription?.subscriptionUnit === 'DAY') {
        subscriptionDiscountEndsAt = startingTime.plus({
          days: activeSubscription?.subscriptionDuration,
        });
      } else if (activeSubscription?.subscriptionUnit === 'MONTH') {
        subscriptionDiscountEndsAt = startingTime.plus({
          months: activeSubscription?.subscriptionDuration,
        });
      } else if (activeSubscription?.subscriptionUnit === 'YEAR') {
        subscriptionDiscountEndsAt = startingTime.plus({
          years: activeSubscription?.subscriptionDuration,
        });
      } else {
        console.error('No member.subscription info found');
      }
    }

    if (!subscriptionDiscountEndsAt) {
      console.error('subscriptionDiscountEndsAt has not been defined');
      return;
    }

    if (
      currentDate >= subscriptionDiscountEndsAt ||
      subscriptionDiscountEndsAt === 'No subsequent discount applicable'
    ) {
      subscriptionDiscountHasEnded = true;
    } else {
      subscriptionDiscountHasEnded = false;
    }

    if (discountInfo?.hasRenewed) {
      const price = new Decimal(activeSubscription?.initialSubscriptionPrice);
      const discount = new Decimal(
        discountInfo?.subsequentSubscriptionDiscountAmount,
      );

      if (discountInfo?.subsequentSubscriptionDiscountType === 'FLATAMOUNT') {
        subscriptionPriceAfterDiscount = price.minus(discount);
      } else if (
        discountInfo?.subsequentSubscriptionDiscountType === 'PERCENTAGE'
      ) {
        const discountValue = price.mul(discount.div(100));
        subscriptionPriceAfterDiscount = price.minus(discountValue);
      } else if (
        discountInfo?.subsequentSubscriptionDiscountType === 'PRICEOVERRIDE'
      ) {
        subscriptionPriceAfterDiscount = new Decimal(
          discountInfo?.subsequentSubscriptionDiscountAmount,
        );
      } else {
        console.error(
          'Issue with discountInfo subsequentSubscriptionDiscountType',
        );
      }
    } else {
      const price = new Decimal(activeSubscription?.initialSubscriptionPrice);
      const discount = new Decimal(
        discountInfo?.initialSubscriptionDiscountAmount,
      );

      if (discountInfo?.initialSubscriptionDiscountType === 'FLATAMOUNT') {
        subscriptionPriceAfterDiscount = price.minus(discount);
      } else if (
        discountInfo?.initialSubscriptionDiscountType === 'PERCENTAGE'
      ) {
        const discountValue = price.mul(discount.div(100));
        subscriptionPriceAfterDiscount = price.minus(discountValue);
      } else if (
        discountInfo?.initialSubscriptionDiscountType === 'PRICEOVERRIDE'
      ) {
        subscriptionPriceAfterDiscount = new Decimal(
          discountInfo?.initialSubscriptionDiscountAmount,
        );
      } else {
        console.error(
          'Issue with discountInfo initialSubscriptionDiscountType',
        );
      }
    }

    let initialWalletDiscountHasEnded;
    let initialWalletDiscountEndsAt;
    if (activeSubscription?.walletUnit === 'DAY') {
      initialWalletDiscountEndsAt = startingTime.plus({
        days: activeSubscription?.walletDuration,
      });
    } else if (activeSubscription?.walletUnit === 'MONTH') {
      initialWalletDiscountEndsAt = startingTime.plus({
        months: activeSubscription?.walletDuration,
      });
    } else if (activeSubscription?.walletUnit === 'YEAR') {
      initialWalletDiscountEndsAt = startingTime.plus({
        years: activeSubscription?.walletDuration,
      });
    }

    initialWalletDiscountHasEnded = currentDate >= initialWalletDiscountEndsAt;

    if (initialWalletDiscountHasEnded) {
      if (
        Number(discountInfo.subsequentWalletDiscountAmount === 0) &&
        Number(discountInfo.subsequentWalletDiscountDurationAmount === 200) &&
        discountInfo.subsequentWalletDiscountDurationUnit === 'YEAR'
      ) {
        walletDiscountEndsAt = 'No subsequent discount applicable';
        walletDiscountHasEnded = true;
      } else if (discountInfo?.subsequentWalletDiscountDurationUnit === 'DAY') {
        walletDiscountEndsAt = startingTime.plus({
          days: discountInfo.subsequentWalletDiscountDurationAmount,
        });
      } else if (
        discountInfo?.subsequentWalletDiscountDurationUnit === 'MONTH'
      ) {
        walletDiscountEndsAt = startingTime.plus({
          months: discountInfo.subsequentWalletDiscountDurationAmount,
        });
      } else if (
        discountInfo?.subsequentWalletDiscountDurationUnit === 'YEAR'
      ) {
        walletDiscountEndsAt = startingTime.plus({
          years: discountInfo.subsequentWalletDiscountDurationAmount,
        });
      } else {
        walletDiscountEndsAt = 'N/A'; //Means wallet spend isn't applicable to this tier
      }
    } else {
      walletDiscountEndsAt = initialWalletDiscountEndsAt;
    }

    walletDiscountHasEnded = currentDate >= walletDiscountEndsAt;

    if (
      (walletDiscountHasEnded === true &&
        walletDiscountEndsAt === 'No subsequent discount applicable') ||
      (walletDiscountHasEnded === true &&
        initialWalletDiscountHasEnded === false) ||
      (walletDiscountHasEnded === false &&
        initialWalletDiscountHasEnded === false)
    ) {
      const price = new Decimal(activeSubscription?.initialWalletPrice);
      const discount = new Decimal(discountInfo?.initialWalletDiscountAmount);

      if (discountInfo?.initialWalletDiscountType === 'FLATAMOUNT') {
        walletPriceAfterDiscount = price.minus(discount);
      } else if (discountInfo?.initialWalletDiscountType === 'PERCENTAGE') {
        const discountValue = price.mul(discount.div(100));
        walletPriceAfterDiscount = price.minus(discountValue);
      } else if (discountInfo?.initialWalletDiscountType === 'PRICEOVERRIDE') {
        walletPriceAfterDiscount = new Decimal(
          discountInfo?.initialWalletDiscountAmount,
        );
      }
    } else if (
      walletDiscountHasEnded === false &&
      initialWalletDiscountHasEnded === true
    ) {
      const price = new Decimal(activeSubscription?.subsequentWalletPrice);
      const discount = new Decimal(
        discountInfo?.subsequentWalletDiscountAmount,
      );

      if (
        Number(discountInfo.subsequentWalletDiscountAmount === 0) &&
        Number(discountInfo.subsequentWalletDiscountDurationAmount === 200) &&
        discountInfo.subsequentWalletDiscountDurationUnit === 'YEAR'
      ) {
        walletDiscountEndsAt = 'No subsequent discount applicable';
        walletDiscountHasEnded = true;
      } else if (discountInfo?.subsequentWalletDiscountType === 'FLATAMOUNT') {
        walletPriceAfterDiscount = price.minus(discount);
      } else if (discountInfo?.subsequentWalletDiscountType === 'PERCENTAGE') {
        const discountValue = price.mul(discount.div(100));
        walletPriceAfterDiscount = price.minus(discountValue);
      } else if (
        discountInfo?.subsequentWalletDiscountType === 'PRICEOVERRIDE'
      ) {
        walletPriceAfterDiscount = new Decimal(
          discountInfo?.subsequentWalletDiscountAmount,
        );
      }
    }

    const updatedDiscountInfo = {
      ...discountInfo,
      subscriptionDiscountHasEnded,
      subscriptionDiscountEndsAt,
      subscriptionPriceAfterDiscount,
      initialWalletDiscountHasEnded,
      initialWalletDiscountEndsAt,
      walletDiscountHasEnded,
      walletDiscountEndsAt,
      walletPriceAfterDiscount,
    };
    setActor({
      ...actor,
      discountInfo: updatedDiscountInfo,
    });
  };

  const handleAdjustUpdate = (e) => {
    let value = e.target.value;
    value = value
      .replace(/[^\d.-]/g, '')
      .replace(/(?<!^)-/g, '')
      .replace(/-+/g, (m, i) => (i === 0 ? '-' : ''))
      .replace(/(\..*)\./g, '$1')
      .replace(/^(-|)(\.)/, '$10$2')
      .replace(/^(-?)0+(?=\d)/, '$1');
    setAdjustAmount(value);
  };
  const getInitialWalletPromoCredit = () => {
    if (
      !actor?.discountInfo?.initialAdjustmentAmount ||
      Number(actor?.discountInfo?.initialAdjustmentAmount) === 0
    ) {
      return 'N/A';
    } else if (Number(actor?.discountInfo?.initialAdjustmentAmount) > 0) {
      //Means discount gives a walletDeposit
      const adjustmentNumberValue = Math.abs(
        Number(actor?.discountInfo?.initialAdjustmentAmount),
      );
      const formatted = adjustmentNumberValue?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
      return formatted;
    } else {
      //Means discount gifted something but the total wallet deposit is less than the tiers deposit amount
      const remainder = new Decimal(activeSubscription?.initialWalletPrice ?? 0)
        .plus(new Decimal(actor?.discountInfo?.initialAdjustmentAmount ?? 0))
        .toNumber();
      const formatted = remainder.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
      return formatted;
    }
  };

  const getQuarterlyMatchAmount = () => {
    const adjustmentAmountRemovesBalance =
      Number(actor?.discountInfo?.subsequentAdjustmentAmount) +
        Number(
          actor?.subscriptions[0]?.subscriptionTier?.subsequentWalletPrice,
        ) ===
      0;
    //If the adjustment amount zeroes out the tiers wallet price it means we dont have a quarterly match
    if (
      !actor?.discountInfo?.subsequentWalletDiscountAmount ||
      Number(actor?.discountInfo?.subsequentWalletDiscountAmount) === 0 ||
      adjustmentAmountRemovesBalance
    ) {
      return 'N/A';
    } else if (Number(actor?.discountInfo?.subsequentAdjustmentAmount) > 0) {
      //Means discount gives a walletDeposit/extra $ than the tier gives by default
      const adjustmentDecimalValue = new Decimal(
        actor?.discountInfo?.subsequentAdjustmentAmount,
      );

      if (Number(actor?.discountInfo?.subsequentWalletDiscountAmount) > 0) {
        //need to factor in this discount to the returned amount
        if (
          actor?.discountInfo?.subsequentWalletDiscountType === 'PERCENTAGE'
        ) {
          const walletPrice = new Decimal(
            activeSubscription?.subsequentWalletPrice,
          );
          const discountPercent = new Decimal(
            actor?.discountInfo?.subsequentWalletDiscountAmount,
          );

          const discountedPrice = walletPrice.minus(
            walletPrice.times(discountPercent).dividedBy(100),
          );
          const totalMatchAfterDiscounts = discountedPrice.plus(
            adjustmentDecimalValue,
          );

          const formatted = totalMatchAfterDiscounts
            ?.toNumber()
            ?.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            });
          return formatted;
        } else if (
          actor?.discountInfo?.subsequentWalletDiscountType === 'FLATAMOUNT'
        ) {
          const amountToAdd = new Decimal(
            actor?.discountInfo?.subsequentWalletDiscountAmount,
          );
          const total = amountToAdd.plus(adjustmentDecimalValue);
          const formatted = total?.toNumber()?.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          });
          return formatted;
        } else if (
          actor?.discountInfo?.subsequentWalletDiscountType === 'PRICEOVERRIDE'
        ) {
          const walletPrice = new Decimal(
            activeSubscription?.subsequentWalletPrice,
          );
          const amountDiscountedFromOverride = walletPrice.minus(
            new Decimal(actor?.discountInfo?.subsequentWalletDiscountAmount),
          );

          const totalMatchAfterDiscounts = amountDiscountedFromOverride.plus(
            adjustmentDecimalValue,
          );

          const formatted = totalMatchAfterDiscounts
            ?.toNumber()
            ?.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            });
          return formatted;
        }
      } else {
        //no subsequentWalletDiscountAmount needs to be calculated
        const formatted = adjustmentDecimalValue
          ?.toNumber()
          ?.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          });
        return formatted;
      }
    } else {
      //Handles cases when subsequentAdjustmentAmount < 0 (when wallet amount remaining is less than Subscription Tier usually gives)
      const discountAmount = new Decimal(
        actor?.discountInfo?.subsequentWalletDiscountAmount,
      ); //will be 0 (number) if subsequentWalletDiscountAmount is "0"

      //this combination of conditions means there is for sure no subsequent Wallet discount unless its in the form of an adjustment amount (handled above)
      const noSubsequentWalletDiscount =
        discountAmount.equals(0) &&
        actor?.discountInfo?.subsequentWalletDiscountType === 'FLATAMOUNT' &&
        Number(
          actor?.discountInfo?.subsequentSubscriptionDiscountDurationAmount ===
            200,
        ) &&
        actor?.discountInfo?.subsequentSubscriptionDiscountDurationUnit ===
          'YEAR';

      if (noSubsequentWalletDiscount) {
        const remainder = new Decimal(
          activeSubscription?.subsequentWalletPrice ?? 0,
        )
          .plus(
            new Decimal(actor?.discountInfo?.subsequentAdjustmentAmount ?? 0),
          )
          .toNumber();
        const formatted = remainder.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        });
        return formatted;
      } else {
        //still need to calculate how much the subsequentWalletDiscountAmount is giving to get total quarterly match
        if (
          actor?.discountInfo?.subsequentWalletDiscountType === 'PERCENTAGE'
        ) {
          const walletPrice = new Decimal(
            activeSubscription?.subsequentWalletPrice,
          );
          const discountPercent = new Decimal(
            actor?.discountInfo?.subsequentWalletDiscountAmount,
          );

          const discountedPrice = walletPrice.minus(
            walletPrice.times(discountPercent).dividedBy(100),
          );
          const formatted = discountedPrice
            ?.toNumber()
            ?.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            });
          return formatted;
        } else if (
          actor?.discountInfo?.subsequentWalletDiscountType === 'FLATAMOUNT'
        ) {
          const discountedPrice = new Decimal(
            actor?.discountInfo?.subsequentWalletDiscountAmount,
          );
          const formatted = discountedPrice
            ?.toNumber()
            ?.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            });
          return formatted;
        } else if (
          actor?.discountInfo?.subsequentWalletDiscountType === 'PRICEOVERRIDE'
        ) {
          const walletPrice = new Decimal(
            activeSubscription?.subsequentWalletPrice,
          );
          const discountedPrice = walletPrice.minus(
            new Decimal(actor?.discountInfo?.subsequentWalletDiscountAmount),
          );
          const formatted = discountedPrice
            ?.toNumber()
            ?.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            });
          return formatted;
        }
      }
    }
  };

  // Renewal dates are derived on read — nextSubscriptionRenewalDate by the API
  // and nextSubscriptionRenewal by the fish context — and neither is accepted
  // back by the update schema.
  const stripNextRenewal = (sub) => {
    if (!sub) return sub;

    if (Array.isArray(sub)) {
      return sub.map(
        ({ nextSubscriptionRenewal, nextSubscriptionRenewalDate, ...s }) => s,
      );
    }
    const { nextSubscriptionRenewal, nextSubscriptionRenewalDate, ...s } = sub;
    return s;
  };

  const handleCloudinaryUpload = async (e) => {
    const file = e.target.files?.[0];
    // Selecting nothing (cancelling the picker) still fires change.
    if (!file) return;

    const basePath = file.name.split(/[/\\]/).pop() || '';

    const sanitized = basePath.replace(/[^a-zA-Z0-9._-]/g, '_');
    const sanitizedFile = new File([file], sanitized, { type: file.type });
    const formData = new FormData();

    if (file) {
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/svg+xml',
        'image/gif',
        'image/jpg',
        'image/heic',
        'image/webp',
        'image/avif',
      ];

      if (!allowedTypes.includes(file.type)) {
        toastError(`Unsupported image type: ${file.type || 'unknown'}`);
        return;
      }

      formData.append('images', sanitizedFile);
      formData.append(
        'assetTags',
        JSON.stringify([{ name: 'Member Profile' }]),
      );

      const res = await service.uploadFishProfileImage(actor.id, formData);
      if (res.ok) {
        const data = await res.json();
        const actorWithAssetId = {
          ...actor,
          profileImageId: data.id,
        };
        setActor(actorWithAssetId);

        const {
          allPurchasesTotal,
          discountInfo,
          monthToDateSpend,
          pairingCode,
          purchasesArray,
          subscriptions,
          yearToDateSpend,
          mostRecentAsset,
          ...rest
        } = actorWithAssetId;

        const cleanedActorObject = {
          ...rest,
          subscriptions: stripNextRenewal(subscriptions),
        };

        const updatedActorRes = await service.updateFish(
          actor.id,
          cleanedActorObject,
        );
        if (updatedActorRes?.ok) {
          const updatedActorData = await updatedActorRes.json();
          //to keep all the previous frontend only state + any updates from updateFish
          setActor((prev) => {
            const recombined = {
              ...prev,
              ...updatedActorData,
              profileImageId: data.id,
            };

            if (prev.subscriptions?.length) {
              recombined.subscriptions = prev.subscriptions.map(
                (prevSub, i) => ({
                  ...prevSub,
                  ...(updatedActorData.subscriptions?.[i] ?? {}),
                }),
              );
            }
            return recombined;
          });
        }
      } else {
        toastError('Could not upload the profile image.');
      }
    }

    // Allow re-selecting the same file after a failed attempt.
    e.target.value = '';
  };

  const handleHomeBaseUpdate = async () => {
    const {
      allPurchasesTotal,
      discountInfo,
      monthToDateSpend,
      pairingCode,
      purchasesArray,
      subscriptions,
      yearToDateSpend,
      mostRecentAsset,
      permissions,
      ...rest
    } = actor;
    const cleanedActorObject = {
      ...rest,
      subscriptions: stripNextRenewal(subscriptions),
    };

    const updatedActorRes = await service.updateFish(
      actor.id,
      cleanedActorObject,
    );

    if (updatedActorRes?.ok) {
      const updatedActorData = await updatedActorRes.json();
      //to keep all the previous frontend only state + any updates from updateFish
      setActor((prev) => {
        const recombined = {
          ...prev,
          ...updatedActorData,
        };

        if (prev.subscriptions?.length) {
          recombined.subscriptions = prev.subscriptions.map((prevSub, i) => ({
            ...prevSub,
            ...(updatedActorData.subscriptions?.[i] ?? {}),
          }));
        }
        return recombined;
      });
      setHomeBaseMessage('Homebase successfully updated');
    } else {
      console.error('Issue while updating homebase');
      setHomeBaseMessage('Error while updating Homebase');
    }
    setIsEditing(false);
  };

  const homeBaseDisplayHandler = (homeBaseString) => {
    const underscore = homeBaseString.indexOf('_');

    const firstWord = homeBaseString.slice(0, underscore).toLowerCase();
    const secondWord = homeBaseString
      .slice(underscore + 1, homeBaseString.length)
      .toLowerCase();

    const formatted =
      firstWord.charAt(0).toUpperCase() +
      firstWord.slice(1) +
      ' ' +
      secondWord.charAt(0).toUpperCase() +
      secondWord.slice(1);
    return formatted;
  };

  const handleTabsValueChange = async (value) => {
    if (value === 'connections') {
      try {
        setIsLoading(true);
        const sharedSubsData = await service.getSharedSubscription(
          actor.id,
          actor?.subscriptions[0]?.id,
        );
        if (sharedSubsData?.data) {
          const otherFishOnSubscription = sharedSubsData?.data?.filter(
            (fish) => fish.fishId !== actor.id,
          );
          setOtherFishOnSubscription(otherFishOnSubscription);
        }
      } catch (e) {
        console.error(`Error during Tabs change: ${e}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleUpdateSharedSubscription = async (isDeleting) => {
    try {
      setIsLoading(true);
      let data;

      if (isDeleting) {
        data = await service.deleteSharedSubscription(
          actor?.subscriptions[0]?.id,
          otherFishOnSubscription[fishCardIndex]?.fishId,
          user?.email,
        );
      } else {
        data = await service.updateSharedSubscription(
          actor?.id,
          actor?.subscriptions[0]?.id,
          selectedFishSubToAdd?.id,
        );
      }

      if (data) {
        const refreshedData = await service.getSharedSubscription(
          actor?.id,
          actor?.subscriptions[0]?.id,
        );
        if (refreshedData?.data) {
          const otherFishOnSubscription = refreshedData?.data?.filter(
            (fish) => fish.fishId !== actor.id,
          );
          setOtherFishOnSubscription(otherFishOnSubscription);
          setSharedTabMessage('*Successfully updated the shared subscription');
        }
      } else {
        setSharedTabMessage(
          '*Error occured while updating shared subscription',
        );
      }
    } catch (e) {
      console.error(`Error updating shared subscription: ${e}`);
    } finally {
      setIsLoading(false);
      setAddDialogOpen(false);
      setDeleteDialogOpen(false);
      setSelectedFishSubToAdd(null);
    }
  };

  const handleUpdateSharedSubscriptionAccess = async (
    secondaryFishId,
    access,
  ) => {
    const subscription = actor?.subscriptions?.[0];
    if (!subscription) return;

    try {
      const updatedLink = await service.updateSharedSubscriptionAccess(
        subscription.fishId,
        subscription.id,
        secondaryFishId,
        access,
      );

      if (subscription.fishId === actor.id) {
        const refreshedData = await service.getSharedSubscription(
          actor.id,
          subscription.id,
        );
        if (refreshedData?.data) {
          setOtherFishOnSubscription(
            refreshedData.data.filter((fish) => fish.fishId !== actor.id),
          );
        }
      } else {
        setActor((prev) => ({
          ...prev,
          sharedSubscription: prev.sharedSubscription.map((link) =>
            link.fishId === secondaryFishId ? { ...link, ...updatedLink } : link,
          ),
        }));
        fetchPaymentSources();
      }
      toastSuccess('Payment access updated');
    } catch (e) {
      console.error(`Error updating payment access: ${e}`);
      toastError('Could not update payment access');
    }
  };

  const handleCancelSubscription = async () => {
    if (!activeSubscription?.id) {
      toastError('Missing necessary arguments to cancel subscription');
    }
    const data = await service.cancelSubscription(activeSubscription?.id);
    if (data) {
      setIsCancelSubOpen(false);
      toastSuccess(
        'Subscription will be cancelled at the end of the current billing cycle.',
      );
      const updatedStatus = getDisplayMemberSubscriptionStatus([data]);
      setDisplaySubscriptionStatus(updatedStatus);
    }
  };

  const handleCancelSubOpenChange = (open) => {
    if (!open) {
      setIsCancelSubOpen(false);
    }
  };

  const value = {
    employeeProfile,
    isTeamMemberRoute,
    activeSubscription,
    activity,
    actor,
    addDialogOpen,
    adjustAmount,
    adjustData,
    adjustDisabled,
    adjustError,
    adjustSuccess,
    adjustWallet,
    aiSummary,
    allMessages,
    cachedFish,
    closeModal,
    confirmCancelText,
    deleteDialogOpen,
    deleteNameForSharedSub,
    detailsType,
    displaySubscriptionStatus,
    dryError,
    dryRunAdjustWallet,
    fetchActor,
    fetchAllInboxes,
    fetchInboxSmsThreads,
    fetchSmsThread,
    fetchPaymentSources,
    finalizeAdjustDisabled,
    fishCardIndex,
    fishOptions,
    capturedForms,
    fishTags,
    formatDisplayNameForFishCard,
    fullContact,
    getFishInteractions,
    getInitialWalletPromoCredit,
    getMemberById,
    getMemberSpendAmounts,
    getPreferredProfile,
    getPriceAfterDiscount,
    getPurchaseTypeDisplay,
    getQuarterlyMatchAmount,
    handleAdjustUpdate,
    handleCancelSubOpenChange,
    handleCancelSubscription,
    handleCloudinaryUpload,
    handleHomeBaseUpdate,
    handleInboxChange,
    handleOpenChange,
    handleTabsValueChange,
    handleUpdateSharedSubscription,
    handleUpdateSharedSubscriptionAccess,
    hasFetchedActor,
    hasSubs,
    homeBaseCopy,
    homeBaseDisplayHandler,
    homeBaseMessage,
    homeBaseOptions,
    id,
    inboxes: permittedInboxes,
    interactions,
    isActivityLoading,
    isCancelSubOpen,
    isConfirmOpen,
    isEditing,
    isLoading,
    isSearching,
    modalContent,
    navigate,
    notes,
    openModal,
    otherFishOnSubscription,
    pathname,
    paymentSources,
    preferredProfile,
    purchases,
    readFishTags,
    readSegmentsForFish,
    reason,
    receiptData,
    segmentsForFish,
    selectedFishSubToAdd,
    selectedHomeBase,
    selectedInbox,
    setActor,
    setAddDialogOpen,
    setAdjustAmount,
    setAdjustData,
    setAdjustDisabled,
    setAdjustError,
    setAdjustSuccess,
    setAllMessages,
    setConfirmCancelText,
    setDeleteDialogOpen,
    setDeleteNameForSharedSub,
    setDetailsType,
    setDisplaySubscriptionStatus,
    setDryError,
    setFinalizeAdjustDisabled,
    setFishCardIndex,
    setHasFetchedActor,
    setHomeBaseCopy,
    setHomeBaseMessage,
    setInteractions,
    setIsCancelSubOpen,
    setIsConfirmOpen,
    setIsEditing,
    setIsLoading,
    setModalContent,
    setNotes,
    setOtherFishOnSubscription,
    setPurchases,
    setReason,
    setReceiptData,
    setSelectedFishSubToAdd,
    setSelectedInbox,
    setSharedSubInputValue,
    setSharedSubSearchTerm,
    setSharedTabMessage,
    setShowCancelled,
    setSmsThread,
    sharedSubInputValue,
    sharedSubSearchTerm,
    sharedSubTimeoutRef,
    sharedTabMessage,
    showCancelled,
    smsThread,
    status,
    stripNextRenewal,
    upcomingActivityCount,
    user,
    walletBalance,
  };

  return (
    <FishDetailsContext.Provider value={value}>
      {children}
    </FishDetailsContext.Provider>
  );
}
